import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { NavLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CardContent,
  Grid as MuiGrid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Button as MuiButton,
  CardActions,
  CardHeader,
  Avatar,
} from "@mui/material";
import { Field, Formik } from "formik";
import { spacing, sizing, display, flexbox } from "@mui/system";
import axios from "../../utils/axios";
import {
  GAME_USER_MODIFY,
  SUPPORT_APP_GAME_MODIFY,
  SUPPORT_APP_GAME_MODIFY_IMAGE,
} from "../../constants";
import HomeIcon from "@mui/icons-material/Home";
import useAuth from "../../hooks/useAuth";
import { checkPermissions } from "../../utils/checkPermissions";
import { CircularIndeterminate } from "../components/Progress";
import {
  FieldAPIKey,
  FieldAppID,
  FieldConfigVersions,
  FieldDatabaseUrl,
  FieldDeeplinkOpenApp,
  FieldFirebaseServiceAccountJSON,
  FieldGameServers,
  FieldHost,
  FieldIsSecure,
  FieldName,
  FieldPort,
  FieldStorageBucket,
} from "./components/Fields";
import { normalizeGameObject, validationSchema } from "./utils";
import FieldView from "./components/FieldView";
import FieldViewVersions from "./components/FieldViewVersions";
import axiosFormData from "../../utils/axiosFormData";
import CardMenu from "../../components/CardMenu";
import DropzoneDialog from "../../components/DropZoneDialog";
import useConfirm from "../../components/useConfirm";

const Card = styled(MuiCard)(spacing, sizing, display, flexbox);

const Grid = styled(MuiGrid)(spacing, sizing, display, flexbox);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing, sizing, display, flexbox);

const Alert = styled(MuiAlert)(spacing);

const Button = styled(MuiButton)(spacing);

// TODO: Create proper loading state UI for this
function GamesView() {
  const { gameId } = useParams();
  const { permissions, app, selectApp } = useAuth();
  const [edit, setEdit] = useState(false);
  const [openLogo, setOpenLogo] = useState(false);
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [game, setGame] = useState({
    app_id: null,
    firebase_service_account_json: null,
    api_key: null,
    database_url: null,
    game_servers: null,
    deeplink_open_app: null,
    is_secure: null,
    host: null,
    port: null,
    storage_bucket: null,
    config_versions: null,
  });

  const checkCurrentApp = useCallback(
    (game) => {
      if (app.id === game.id) {
        selectApp(game);
      }
    },
    [selectApp, app.id]
  );

  const getGame = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(`/api/v1/games/${gameId}`);

      setGame(normalizeGameObject(data));

      setLoading(false);
    } catch (error) {
      setError(error.messages);

      setLoading(false);

      console.log(error);
    }
    setLoading(false);
  }, [setLoading, setError, setGame, gameId]);

  const [ConfirmDialog, confirmAction] = useConfirm(
    "Are you sure you want to update game?"
  );
  const updateGame = useCallback(
    async (
      {
        firebase_service_account_json,
        game_servers,
        config_versions,
        ...values
      },
      { setErrors, setStatus, setSubmitting }
    ) => {
      const isConfirmed = await confirmAction();

      if (!isConfirmed) {
        return;
      }

      try {
        const { data } = await axios.post(`/api/v1/games/${gameId}`, {
          firebase_service_account_json: JSON.parse(
            firebase_service_account_json
          ),
          game_servers: JSON.parse(game_servers),
          config_versions: config_versions.reduce(
            (configVersions, version) => ({
              ...configVersions,
              [version.key]: version.value,
            }),
            {}
          ),
          ...values,
        });

        checkCurrentApp(data);

        setGame(normalizeGameObject(data));

        setEdit(false);

        setLoading(false);
      } catch (error) {
        console.log(error);

        const message =
          (error.messages && error.messages.join("\n")) ||
          "Something went wrong";

        setStatus({ success: false });
        setErrors({ submit: message });
        setSubmitting(false);

        setLoading(false);
      }
    },
    [checkCurrentApp, gameId]
  );

  const updateLogo = useCallback(
    async ({ logo }) => {
      try {
        setLoading(true);

        const { data } = await axiosFormData.post(
          `/api/v1/games/${gameId}/logo`,
          {
            image: logo[0],
          }
        );

        checkCurrentApp(data);

        setGame(normalizeGameObject(data));

        setLoading(false);
      } catch (error) {
        setError(error.messages);

        setLoading(false);

        console.log(error);
      }
    },
    [checkCurrentApp, gameId]
  );

  const updateImagePreview = useCallback(
    async ({ imagePreview }) => {
      try {
        setLoading(true);

        const { data } = await axiosFormData.post(
          `/api/v1/games/${gameId}/image_preview`,
          {
            image: imagePreview[0],
          }
        );

        checkCurrentApp(data);

        setGame(normalizeGameObject(data));

        setLoading(false);
      } catch (error) {
        setError(error.messages);

        setLoading(false);

        console.log(error);
      }
    },
    [checkCurrentApp, gameId]
  );

  useEffect(getGame, [getGame]);

  if (loading) {
    return <CircularIndeterminate />;
  }

  if (error) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="Game View" />

      <Typography variant="h3" gutterBottom display="inline">
        Game {gameId}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/games">
          Games
        </Link>
        <Typography>{gameId}</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Card>
        <CardHeader
          title={game.name}
          avatar={<Avatar src={game.logo} variant="square" />}
          action={
            <CardMenu
              menuItems={[
                {
                  key: "edit",
                  onClick: () => setEdit(true),
                  text: "Edit",
                  disabled: !checkPermissions(
                    [SUPPORT_APP_GAME_MODIFY],
                    permissions
                  ),
                },
                {
                  key: "open_logo",
                  onClick: () => setOpenLogo(true),
                  text: "Update Logo",
                  disabled: !checkPermissions(
                    [SUPPORT_APP_GAME_MODIFY_IMAGE],
                    permissions
                  ),
                },
                {
                  key: "open__image_preview",
                  onClick: () => setOpenImagePreview(true),
                  text: "Update Image Preview",
                  disabled: !checkPermissions(
                    [SUPPORT_APP_GAME_MODIFY_IMAGE],
                    permissions
                  ),
                },
              ]}
              disabled={
                !checkPermissions([GAME_USER_MODIFY], permissions) ||
                loading ||
                edit
              }
            />
          }
        />
        <Formik
          initialValues={{
            app_id: game.app_id,
            firebase_service_account_json: game.firebase_service_account_json,
            api_key: game.api_key,
            database_url: game.database_url,
            game_servers: game.game_servers,
            deeplink_open_app: game.deeplink_open_app,
            name: game.name,
            is_secure: game.is_secure,
            host: game.host,
            port: game.port,
            storage_bucket: game.storage_bucket,
            config_versions: game.config_versions || [],
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={updateGame}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            resetForm,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => {
            return (
              <form noValidate onSubmit={handleSubmit}>
                <CardContent>
                  <Grid container spacing={4} alignItems="center">
                    {errors.submit && (
                      <Grid item xs={12}>
                        <Alert severity="warning">{errors.submit}</Alert>
                      </Grid>
                    )}
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldAppID />
                      ) : (
                        <FieldView title="App ID" value={game.app_id} />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldName />
                      ) : (
                        <FieldView title="Name" value={game.name} />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldAPIKey />
                      ) : (
                        <FieldView title="API key" value={game.api_key} />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldDeeplinkOpenApp />
                      ) : (
                        <FieldView
                          title="Deeplink open app"
                          value={game.deeplink_open_app}
                        />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldStorageBucket />
                      ) : (
                        <FieldView
                          title="Storage bucket"
                          value={game.storage_bucket}
                        />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldDatabaseUrl />
                      ) : (
                        <FieldView
                          title="Database URL"
                          value={game.database_url}
                        />
                      )}
                    </Grid>
                    <Grid item md={6} xs={5}>
                      {edit ? (
                        <FieldHost />
                      ) : (
                        <FieldView title="Host" value={game.host} />
                      )}
                    </Grid>
                    <Grid item md={6} xs={5}>
                      {edit ? (
                        <FieldPort />
                      ) : (
                        <FieldView title="Port" value={game.port} />
                      )}
                    </Grid>
                    <Grid item md={6} xs={2}>
                      {edit ? (
                        <FieldIsSecure />
                      ) : (
                        <FieldView title="Is secure" value={game.is_secure} />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldFirebaseServiceAccountJSON />
                      ) : (
                        <FieldView
                          title="Firebase service account JSON"
                          value={values.firebase_service_account_json}
                        />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldGameServers />
                      ) : (
                        <FieldView
                          title="Game servers"
                          value={values.game_servers}
                        />
                      )}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {edit ? (
                        <FieldConfigVersions value={values.config_versions} />
                      ) : (
                        <FieldViewVersions
                          title="Versions"
                          versions={values.config_versions}
                        />
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
                {edit && (
                  <CardActions>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Update Game
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => {
                        resetForm();
                        setEdit(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </CardActions>
                )}
              </form>
            );
          }}
        </Formik>
      </Card>
      {openLogo && (
        <Formik
          initialValues={{
            logo: game.logo,
          }}
          enableReinitialize
          onSubmit={updateLogo}
        >
          {({ handleSubmit, isSubmitting }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Field name="logo">
                {({
                  form: { setFieldValue, submitForm },
                  field: { name, onBlur, value },
                  meta: { error, touched },
                }) => {
                  return (
                    <DropzoneDialog
                      avatar={game.logo}
                      // Default props
                      open={openLogo}
                      onClose={() => {
                        setOpenLogo(false);
                      }}
                      onSave={() => {
                        submitForm();
                        setOpenLogo(false);
                      }}
                      filesLimit={1}
                      acceptedFiles={["image/jpeg", "image/png", "image/bmp"]}
                      maxFileSize={3000000}
                      showAlerts={false}
                      // Key props
                      dialogTitle="Upload Logo"
                      value={value}
                      onChange={(acceptedFiles) => {
                        setFieldValue("logo", acceptedFiles);
                      }}
                      helperText={touched || error}
                      inputProps={{
                        name,
                        onBlur,
                      }}
                      // Meta props
                      error={touched && error}
                      disabled={isSubmitting}
                      // Other props
                      /*{...props}*/
                    />
                  );
                }}
              </Field>
            </form>
          )}
        </Formik>
      )}
      {openImagePreview && (
        <Formik
          initialValues={{
            imagePreview: game.imagePreview,
          }}
          enableReinitialize
          onSubmit={updateImagePreview}
        >
          {({ handleSubmit, isSubmitting }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Field name="logo">
                {({
                  form: { setFieldValue, submitForm },
                  field: { name, onBlur, value },
                  meta: { error, touched },
                }) => {
                  return (
                    <DropzoneDialog
                      avatar={game.imagePreview}
                      // Default props
                      open={openImagePreview}
                      onClose={() => {
                        setOpenImagePreview(false);
                      }}
                      onSave={() => {
                        submitForm();
                        setOpenImagePreview(false);
                      }}
                      filesLimit={1}
                      acceptedFiles={["image/jpeg", "image/png", "image/bmp"]}
                      maxFileSize={3000000}
                      showAlerts={false}
                      // Key props
                      dialogTitle="Upload Image Preview"
                      value={value}
                      onChange={(acceptedFiles) => {
                        setFieldValue("imagePreview", acceptedFiles);
                      }}
                      helperText={touched || error}
                      inputProps={{
                        name,
                        onBlur,
                      }}
                      // Meta props
                      error={touched && error}
                      disabled={isSubmitting}
                      // Other props
                      /*{...props}*/
                    />
                  );
                }}
              </Field>
            </form>
          )}
        </Formik>
      )}
      <ConfirmDialog />
    </React.Fragment>
  );
}

export default GamesView;
